import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, Award, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function CertificatePage() {
  const { moduleId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const certRef = useRef(null);

  const generateCert = useCallback(async () => {
    try {
      const { data } = await api('post', `/certificates/generate/${moduleId}`);
      setCertificate(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate certificate');
    } finally {
      setLoading(false);
    }
  }, [api, moduleId]);

  useEffect(() => { generateCert(); }, [generateCert]);

  const handlePrint = () => {
    const content = certRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const doc = printWindow.document;
    doc.open();
    const fontLink = doc.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Manrope:wght@400;500;600&display=swap';
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
    const style = doc.createElement('style');
    style.textContent = 'body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f4; } @media print { body { background: white; } }';
    doc.head.appendChild(style);
    doc.title = 'GrillSmith Certificate';
    const container = doc.createElement('div');
    container.innerHTML = content.outerHTML;
    doc.body.appendChild(container);
    doc.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-[#D9480F] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="pb-20 md:pb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-stone-600 hover:text-stone-900 mb-4 -ml-2 rounded-xl">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Card className="bg-white rounded-2xl border border-stone-200">
          <CardContent className="p-8 text-center">
            <Award className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">Complete the training module to earn a certificate</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="pb-20 md:pb-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-stone-600 hover:text-stone-900 -ml-2 rounded-xl" data-testid="back-from-cert">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button onClick={handlePrint} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="print-cert-btn">
            <Download className="w-4 h-4 mr-1" /> Print Certificate
          </Button>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
        <div ref={certRef} data-testid="certificate-view"
          style={{
            maxWidth: '700px', margin: '0 auto', padding: '48px', background: 'white',
            border: '3px solid #D9480F', borderRadius: '16px', position: 'relative', overflow: 'hidden',
            fontFamily: "'Manrope', sans-serif"
          }}>
          {/* Decorative corners */}
          <div style={{ position: 'absolute', top: '8px', left: '8px', right: '8px', bottom: '8px', border: '1px solid #E7E5E4', borderRadius: '12px', pointerEvents: 'none' }} />
          
          <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            {/* Logo */}
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', background: '#D9480F', borderRadius: '12px', marginBottom: '16px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
              </svg>
            </div>

            <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '28px', fontWeight: 700, color: '#1C1917', margin: '0 0 4px', letterSpacing: '-0.5px' }}>
              GRILLSMITH
            </h1>
            <p style={{ fontSize: '11px', color: '#A8A29E', textTransform: 'uppercase', letterSpacing: '3px', margin: '0 0 24px' }}>
              Craft By Fire
            </p>

            <div style={{ width: '60px', height: '2px', background: '#D9480F', margin: '0 auto 24px' }} />

            <p style={{ fontSize: '12px', color: '#78716C', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 8px' }}>
              Certificate of Completion
            </p>

            <p style={{ fontSize: '14px', color: '#57534E', margin: '0 0 12px' }}>
              This certifies that
            </p>

            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '32px', fontWeight: 700, color: '#D9480F', margin: '0 0 12px' }}>
              {certificate.user_name}
            </h2>

            <p style={{ fontSize: '14px', color: '#57534E', margin: '0 0 24px', lineHeight: '1.6' }}>
              has successfully completed the training module
            </p>

            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '20px', fontWeight: 600, color: '#1C1917', margin: '0 0 8px', padding: '12px 24px', background: '#FFF4E6', borderRadius: '12px', display: 'inline-block' }}>
              {certificate.module_title}
            </h3>

            {certificate.test_score && (
              <p style={{ fontSize: '14px', color: '#57534E', margin: '16px 0 0' }}>
                with a test score of <strong style={{ color: '#D9480F' }}>{certificate.test_score}%</strong>
              </p>
            )}

            <div style={{ width: '60px', height: '2px', background: '#E7E5E4', margin: '24px auto' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '24px' }}>
              <div style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '12px', color: '#78716C', margin: '0' }}>Issue Date</p>
                <p style={{ fontSize: '14px', color: '#1C1917', fontWeight: 600, margin: '2px 0 0' }}>
                  {new Date(certificate.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: '12px', color: '#78716C', margin: '0' }}>Certificate No.</p>
                <p style={{ fontSize: '14px', color: '#1C1917', fontWeight: 600, margin: '2px 0 0', fontFamily: 'monospace' }}>
                  {certificate.certificate_number}
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
